import { object, string, number, boolean } from 'yup';
import type { TravelOfferDiag, TravelOfferDiagOtaUrls } from './TravelOfferDiag.types';
import { addAction, addError } from '@shared/utils/logger';
import { ensureHttpsPrefix, isPublicWebUrl } from '@shared/utils/urlHelpers';

/** GraphQL `JSON` scalars often deserialize numbers/booleans as non-strings; coerce for diagnostics validation. */
const stringTransform = (value: string, original: unknown) => (original !== null && original !== undefined && typeof original !== 'string' ? String(original) : value);

const numberTransform = (value: number, original: unknown) => {
  if (typeof original === 'string' && original.trim() !== '') {
    const n = Number(original);
    return Number.isFinite(n) ? n : value;
  }
  return value;
};

const diagStringRequired = () => string().transform(stringTransform).required();
const diagStringOptional = () => string().transform(stringTransform);
const diagNumberRequired = () => number().transform(numberTransform).required();
const diagNumberOptional = () => number().transform(numberTransform).notRequired();

const diagBooleanRequired = () =>
  boolean()
    .transform((value, original) => {
      if (original === 'true') return true;
      if (original === 'false') return false;
      return value;
    })
    .required();

const travelOfferDiagOfferSchema = object({
  base: diagStringRequired(),
  total: diagStringRequired(),
  provider: diagStringRequired(),
  nightlyBase: diagStringRequired(),
  nightlyTotal: diagStringRequired(),
  supplierSource: diagStringRequired(),
  perOccupancyNightlyTotal: diagStringRequired(),
  perOccupancyNightlyBase: diagStringRequired(),
  originalBase: diagStringOptional(),
  originalTotal: diagStringOptional(),
  nightlyOriginalBase: diagStringOptional(),
  nightlyOriginalTotal: diagStringOptional(),
  perOccupancyNightlyOriginalTotal: diagStringOptional(),
  perOccupancyNightlyOriginalBase: diagStringOptional(),
});

const travelOfferDiagMarginSchema = object({
  spread: diagStringRequired(),
  markup: diagStringRequired(),
  markupNightly: diagStringRequired(),
  markupPercentOfSpread: diagNumberRequired(),
  markupPerOccupancyNightly: diagStringRequired(),
});

const travelOfferDiagOfferMarginSchema = object({
  distributionMode: diagStringRequired(),
  isWinnerOffer: diagBooleanRequired(),
  markup: diagStringRequired(),
  markupNightly: diagStringRequired(),
  markupPerOccupancyNightly: diagStringRequired(),
  postMarkupBase: diagStringRequired(),
  postMarkupTotal: diagStringRequired(),
  preMarkupBase: diagStringRequired(),
  preMarkupTotal: diagStringRequired(),
});

const travelOfferDiagSpreadAdjustmentSchema = object({
  bestPublishedPrice: diagStringOptional(),
  bestPublishedPriceNightly: diagStringOptional(),
  bestPublishedPriceProvider: diagStringOptional(),
  effectiveCompetitorTotal: diagStringOptional(),
  effectiveCompetitorNightly: diagStringOptional(),
  rawSpread: diagStringRequired(),
  spreadCap: diagStringRequired(),
  finalSpread: diagStringRequired(),
  markupPercentOfSpread: diagNumberOptional(),
  usedPublishedPriceReference: diagBooleanRequired(),
  spreadWasCapped: diagBooleanRequired(),
  maxSpreadPercentUsed: diagNumberRequired(),
});

const travelOfferDiagOtaUrlsSchema = object({
  booking: diagStringOptional().nullable().notRequired(),
  expedia: diagStringOptional().nullable().notRequired(),
});

const travelOfferDiagSchema = object({
  nights: diagNumberRequired(),
  currency: diagStringRequired(),
  occupancy: diagNumberRequired(),
  retailTotal: diagStringRequired(),
  algorithmVersion: diagStringRequired(),
  eligibilityReason: diagStringRequired(),
  margin: travelOfferDiagMarginSchema.required(),
  winner: travelOfferDiagOfferSchema.nullable().default(null),
  secondBest: travelOfferDiagOfferSchema.nullable().default(null),
  spreadAdjustment: travelOfferDiagSpreadAdjustmentSchema.nullable().notRequired().default(null),
  offerMargin: travelOfferDiagOfferMarginSchema.nullable().notRequired().default(null),
  otaUrls: travelOfferDiagOtaUrlsSchema.nullable().notRequired(),
});

const isRecord = (data: unknown): data is Record<string, unknown> => typeof data === 'object' && data !== null && !Array.isArray(data);

const normalizeOtaUrl = (value: unknown): string | null | undefined => {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return undefined;
    }

    return isPublicWebUrl(ensureHttpsPrefix(trimmedValue)) ? trimmedValue : undefined;
  }

  if (value === null) {
    return null;
  }

  return undefined;
};

const normalizeOtaUrls = (value: unknown): TravelOfferDiagOtaUrls | null | undefined => {
  if (value === null) {
    return null;
  }

  if (!isRecord(value)) {
    return undefined;
  }

  const booking = normalizeOtaUrl(value.booking);
  const expedia = normalizeOtaUrl(value.expedia);

  if (booking === undefined && expedia === undefined) {
    return undefined;
  }

  return {
    ...(booking !== undefined ? { booking } : {}),
    ...(expedia !== undefined ? { expedia } : {}),
  };
};

const sanitizeTravelOfferDiagInput = (data: unknown): unknown => {
  if (!isRecord(data)) {
    return data;
  }

  const normalizedOtaUrls = normalizeOtaUrls(data.otaUrls);

  if (normalizedOtaUrls === undefined) {
    const { otaUrls: _otaUrls, ...rest } = data;
    return rest;
  }

  return {
    ...data,
    otaUrls: normalizedOtaUrls,
  };
};

const normalizeParsedTravelOfferDiag = (diag: TravelOfferDiag): TravelOfferDiag => {
  const normalizedOtaUrls = normalizeOtaUrls(diag.otaUrls);

  if (normalizedOtaUrls === undefined) {
    const { otaUrls: _otaUrls, ...rest } = diag;
    return rest;
  }

  return {
    ...diag,
    otaUrls: normalizedOtaUrls,
  };
};

export const validateTravelOfferDiag = (data: unknown): TravelOfferDiag | null => {
  if (!data) return null;

  const sanitizedData = sanitizeTravelOfferDiagInput(data);

  try {
    return normalizeParsedTravelOfferDiag(travelOfferDiagSchema.validateSync(sanitizedData, { abortEarly: false, strict: false }) as TravelOfferDiag);
  } catch (error) {
    addError('validateTravelOfferDiag', { error });
    return null;
  }
};

const isLikelyTravelOfferDiagRoot = (data: unknown): data is TravelOfferDiag => {
  if (!isRecord(data)) return false;
  const record = data as Record<string, unknown>;
  const margin = record.margin;
  if (!isRecord(margin)) return false;
  const m = margin as Record<string, unknown>;
  return (
    typeof record.currency === 'string' &&
    typeof record.eligibilityReason === 'string' &&
    typeof m.markup === 'string' &&
    typeof m.markupNightly === 'string' &&
    typeof m.spread === 'string'
  );
};

export const travelOfferDiagFromApiJson = (data: unknown): TravelOfferDiag | null => {
  const sanitizedData = sanitizeTravelOfferDiagInput(data);

  if (data) {
    try {
      return normalizeParsedTravelOfferDiag(travelOfferDiagSchema.validateSync(sanitizedData, { abortEarly: false, strict: false }) as TravelOfferDiag);
    } catch (error) {
      addAction('travelOfferDiagValidationFailed', { severity: 'warn', error: error instanceof Error ? error.message : String(error) });
    }
  }
  if (isLikelyTravelOfferDiagRoot(sanitizedData)) {
    return sanitizedData;
  }
  return null;
};
